import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import AuthLayout from "@/components/auth/AuthLayout";
import RegisterForm from "@/components/auth/RegisterForm";
import { useAuth } from "@/context/AuthContext";

const SignUp = () => {
  const navigate = useNavigate();
  const { user, isEmployee, loading } = useAuth();

  useEffect(() => {
    const handleAuthRedirect = async (currentUser: any, currentIsEmployee: boolean) => {
      // Don't redirect during password recovery flow
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      if (hashParams.get('type') === 'recovery') return;

      if (currentUser) {
        if (currentIsEmployee) {
          navigate("/employee-portal", { replace: true });
        } else {
          // Check if user has an active subscription first
          const { data: subscription } = await supabase
            .from('user_subscriptions')
            .select('status')
            .eq('user_id', currentUser.id)
            .in('status', ['active', 'trialing'])
            .maybeSingle();

          // No subscription - redirect to plan selection
          // Preserve any plan query param from pricing page
          if (!subscription) {
            const urlParams = new URLSearchParams(window.location.search);
            const planParam = urlParams.get('plan');
            const selectPlanUrl = planParam ? `/select-plan?plan=${planParam}` : '/select-plan';
            navigate(selectPlanUrl, { replace: true });
            return;
          }

          // Check if user has any businesses
          const { data: businesses } = await supabase
            .from('business_users')
            .select('business_id')
            .eq('user_id', currentUser.id)
            .limit(1);

          // Redirect to create-business if no businesses, otherwise to dashboard
          if (businesses && businesses.length === 0) {
            navigate("/create-business", { replace: true });
          } else {
            navigate("/dashboard", { replace: true });
          }
        }
      }
    };

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session) {
          setTimeout(() => {
            handleAuthRedirect(session.user, isEmployee);
          }, 200);
        }
      }
    );

    const checkInitialSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        setTimeout(() => {
          handleAuthRedirect(session.user, isEmployee);
        }, 200);
      }
    };

    if (!loading) {
      checkInitialSession();
    }

    return () => {
      authListener.subscription.unsubscribe();
    };
  }, [navigate, isEmployee, loading]);

  const handleSwitchToSignIn = () => {
    navigate("/sign-in");
  };

  return (
    <AuthLayout title="Create a new account" isSignUp={true}>
      <RegisterForm onSwitchForm={handleSwitchToSignIn} />
    </AuthLayout>
  );
};

export default SignUp;